/**
 * SF-4762 / SF-4811 — the changes to one platform flag (who, when, what).
 * The card shows only the latest change; "View history" opens every row
 * sf-api returns (newest first) in a scrolling pop-up. The parent owns the
 * query so it can reload it after a save; both views read the same rows, so
 * a reload updates the line and an open pop-up together, and a failed
 * reload is noted in both.
 */
import { useState } from 'react';
import { Button, List, Modal, Space, Typography } from 'antd';
import type { PlatformFlagChange } from './api/platformFlagsClient';
import { describeChange, formatDateTime } from './format';

const { Text } = Typography;

export interface FlagHistoryProps {
  /** The flag's key, for test ids. */
  flagKey: string;
  /** The flag's label, for the pop-up title. */
  flagLabel: string;
  rows: PlatformFlagChange[] | undefined;
  isLoading: boolean;
  isError: boolean;
  /** SF-4801 — what list changes are called (default "Exempt list"). */
  listName?: string;
}

const changeLine = (change: PlatformFlagChange, listName?: string) =>
  `${formatDateTime(change.createdAt)} · ${change.changedBy?.email ?? 'Unknown'} · ${describeChange(
    change,
    listName,
  )}`;

export const FlagHistory: React.FC<FlagHistoryProps> = ({
  flagKey,
  flagLabel,
  rows,
  isLoading,
  isError,
  listName,
}) => {
  const [open, setOpen] = useState(false);
  if (isLoading) return <Text type="secondary">Loading recent changes…</Text>;
  // The error state is only for when nothing ever loaded; a failed reload keeps the rows.
  if (isError && !rows) {
    return <Text type="danger">Could not load the recent changes to this flag.</Text>;
  }
  const all = rows ?? [];
  const staleNote = isError && (
    <div>
      <Text type="warning">
        Could not refresh the recent changes. Showing the ones last loaded.
      </Text>
    </div>
  );
  if (all.length === 0) {
    return (
      <>
        {staleNote}
        <Text type="secondary" data-testid={`platform-flag-${flagKey}-latest-change`}>
          No changes yet.
        </Text>
      </>
    );
  }
  const close = () => setOpen(false);
  return (
    <>
      {staleNote}
      <Space size="small" wrap>
        <Text data-testid={`platform-flag-${flagKey}-latest-change`}>
          {changeLine(all[0], listName)}
        </Text>
        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setOpen(true)}>
          View history
        </Button>
      </Space>
      <Modal
        open={open}
        title={`${flagLabel}: history`}
        onCancel={close}
        footer={<Button onClick={close}>Close</Button>}
        styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}
        destroyOnHidden
      >
        {staleNote}
        <List
          size="small"
          data-testid="flag-history"
          dataSource={all}
          renderItem={(change) => (
            <List.Item>
              <Text>{changeLine(change, listName)}</Text>
            </List.Item>
          )}
        />
      </Modal>
    </>
  );
};
